import { CheckCircleOutline } from '@mui/icons-material';
import {
  Icon, MenuItem, TextField 
} from '@mui/material';
import React, {
  type ReactElement, useCallback, useState 
} from 'react';
import { Link } from 'react-router-dom';

import {
  formatCurrency,
  formatDate,
  formatDateString,
  formatInteger,
  formatNumber,
  formatPercent,
  truncateString
} from './utils';

interface CellContentProps {
  activeCell: any;
  columnConfig: any;
  edits: any;
  entity: any;
  handleEditChange: (a: any) => void; // TODO: change type
  links: () => string;
  primaryKey: string;
}

interface Change {
  name: string;
  value: string | number | boolean;
}

interface SelectEditableProps {
  id: string;
  field: string;
  handleEditChange: (a: Change) => void;
  initValue: any;
  options: any; // TODO: options type
}

interface InputEditableProps {
  id: string;
  field: string;
  handleEditChange: (a: Change) => void;
  initValue: any;
}

const formatters = {
  currency: formatCurrency,
  default: (x) => x,
  image_link: (x) => x,
  number: (x) => x,
  integer: formatInteger,
  percent: formatPercent,
  truncate: truncateString,
  datetime: formatDateString,
  date: formatDate,
  double: formatNumber
};

function CellContent({
  activeCell,
  columnConfig,
  edits,
  entity,
  handleEditChange,
  links,
  primaryKey
}: CellContentProps): ReactElement {
  const { key, mutable, oneOf } = columnConfig;

  const id: string = entity[primaryKey];
  const editMode = id === activeCell.id && key === activeCell.key;
  const format = columnConfig.format || 'default';
  const formatter = formatters[format];
  const rawValue = entity[key] || '';
  const edit = edits[id] ? edits[id][key] : '';
  const value = editMode ? edit : formatter(edit || rawValue);
  const link = links ? links[key] : null;

  console.log('CellContent', links);

  if (link) {
    // this will work for any custom content
    return link(entity);
  }

  if (editMode && oneOf) {
    return (
      <SelectEditable
        id={id}
        field={key}
        handleEditChange={handleEditChange}
        options={oneOf}
        initValue={value}
      />
    );
  }

  if (editMode) {
    return (
      <InputEditable id={id} field={key} handleEditChange={handleEditChange} initValue={value} />
    );
  }

  if (format === 'image_link') return <img src={value} alt="image" />;

  const title = mutable ? 'Click to edit.' : '';
  const className = mutable ? 'cursor-pointer' : '';
  const background = edit ? 'yellow' : '';

  // TODO: highlight edited fields
  return (
    <span
      style={{
        background,
        minHeight: '10px',
        minWidth: '20px',
        display: 'inline-block'
      }}
      className={className}
      data-id={mutable ? id : undefined}
      data-key={mutable ? key : undefined}
      title={title}
    >
      {value}
    </span>
  );
}

const SelectEditable = React.memo(function SelectEditable({
  id,
  field,
  handleEditChange,
  initValue,
  options
}: SelectEditableProps) {
  const [ value, setValue ] = useState(initValue);

  const handleSave = useCallback(
    function handleSave(event) {
      event.stopPropagation();

      handleEditChange({ name: `${id}___${field}`, value });
    },
    [ id, field, handleEditChange, value ]
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <TextField
        inputProps={{
          'data-id': id,
          'data-key': field
        }}
        name={`${id}___${field}`}
        onChange={(event) => {
          setValue(event.target.value);
        }}
        onClick={(event) => {
          event.stopPropagation();
        }}
        select
        style={{ width: '100px' }}
        value={value}
        variant="standard"
      >
        {options.map((value, idx) => (
          <MenuItem key={idx} value={value}>
            {value}
          </MenuItem>
        ))}
      </TextField>
      <div style={{ display: 'flex', marginLeft: '4px', flexDirection: 'column' }}>
        <CheckCircleOutline
          className="cursor-pointer"
          fontSize="small"
          style={{ color: 'green' }}
          onClick={handleSave}
        />
        <Icon className="cursor-pointer" fontSize="small" style={{ color: 'red' }}>
          highlight_off
        </Icon>
      </div>
    </div>
  );
});

function InputEditable({
  id,
  field,
  handleEditChange,
  initValue
}: InputEditableProps): ReactElement {
  const [ value, setValue ] = useState(initValue);

  const handleSave = useCallback(
    function handleSave(event) {
      event.stopPropagation();

      handleEditChange({ name: `${id}___${field}`, value });
    },
    [ id, field, handleEditChange, value ]
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <TextField
        inputProps={{
          'data-id': id,
          'data-key': field
        }}
        name={`${id}___${field}`}
        onChange={(event) => {
          setValue(event.target.value);
        }}
        style={{ width: '100px' }}
        value={value}
        variant="standard"
      />
      <div style={{ display: 'flex', marginLeft: '4px', flexDirection: 'column' }}>
        <CheckCircleOutline
          className="cursor-pointer"
          fontSize="small"
          style={{ color: 'green' }}
          onClick={handleSave}
        />
        <Icon className="cursor-pointer" fontSize="small" style={{ color: 'red' }}>
          highlight_off
        </Icon>
      </div>
    </div>
  );
}

export default CellContent;
